import './App.css'
import { Login } from './pages'
import { Route, Switch } from 'wouter'
import AddNewAdminToOrgPage from '@pages/Dashboard/actions/AddNewAdminToOrgPage'
import BillableHoursReportPage from '@pages/Dashboard/reports/BillableHoursReportPage'
import CompletedOrdersReportPage from '@pages/Dashboard/reports/CompletedOrdersReportPage'
import CreateNewOrgPage from '@pages/Dashboard/actions/CreateNewOrgPage'
import DashboardPage from '@pages/Dashboard/DashboardPage'
import NTEInvoiceSearchPage from '@pages/Dashboard/searches/NTEInvoiceSearchPage'
import OverdueOrdersReportPage from '@pages/Dashboard/reports/OverdueOrdersReportPage'
import SendInvoicesPage from '@pages/Dashboard/actions/SendInvoicesPage'
import SubcontractorHoursReportPage from '@pages/Dashboard/reports/SubcontractorHoursReportPage'
import TransferUser from '@pages/Dashboard/actions/TransferUser'
import UsageReportPage from '@pages/Dashboard/reports/UsageReportPage'
import UserPage from '@pages/Dashboard/overviews/UserPage/UserPage'
import UsersOverviewPage from '@pages/Dashboard/overviews/UsersOverviewPage/UsersOverviewPage'
import UserSearchPage from '@pages/Dashboard/searches/UserSearchPage'
import useSession from './hooks/useSession'
import OrganizationsOverviewPage from '@pages/Dashboard/overviews/OrganizationsOverviewPage'
import OrgPage from '@pages/Dashboard/overviews/OrgPage/OrgPage'
import InactiveUsersReport from '@pages/Dashboard/reports/InactiveUsersReport'
import EligibleUsersReportPage from '@pages/Dashboard/reports/EligibleUsersReportPage'
import OrgCollectionsPage from '@pages/Dashboard/overviews/OrgCollectionsPage/OrgCollectionsPage'
import CreateOrgCollectionPage from '@pages/Dashboard/actions/CreateOrgCollectionPage/CreateOrgCollectionPage'
import EditOrgCollectionPage from '@pages/Dashboard/actions/EditOrgCollectionPage/EditOrgCollectionPage'
import SessionsOverviewPage from '@pages/Dashboard/overviews/SessionsOverviewPage'
import PodiumsOverviewPage from '@pages/Dashboard/overviews/PodiumsOverviewPage'
import PodiumPage from '@pages/Dashboard/overviews/PodiumPage/PodiumPage'
import MergeAccountsPage from '@pages/Dashboard/actions/MergeAccountsPage/MergeAccountsPage'
import { ThemeProvider } from '@mui/material/styles'
import theme from './theme'
import GroupPage from '@pages/Dashboard/overviews/GroupPage/GroupPage'
import CreateUKLicensesPage from '@pages/Dashboard/actions/CreateUKLicenses/CreateUKLicenses'
import ManageSsoConnectionsPage from '@pages/Dashboard/actions/ManageSsoConnectionsPage'
import CreateSsoConnectionPage from '@pages/Dashboard/actions/CreateSsoConnectionPage'
import EditSsoConnectionPage from '@pages/Dashboard/actions/EditSsoConnectionPage'
import OnboardSsoTenantPage from '@pages/Dashboard/actions/OnboardSsoTenantPage'
import SsoCallbackPage from '@pages/Dashboard/actions/SsoCallbackPage'
import ReviewOrgsPage from '@pages/Dashboard/actions/ReviewOrgsPage/ReviewOrgsPage'
import ReviewOrgPage from '@pages/Dashboard/actions/ReviewOrgsPage/ReviewOrgPage/ReviewOrgPage'
import OrganizationsDeniedOverviewPage from '@pages/Dashboard/overviews/OrganizationsDeniedOverviewPage'
import OrgXeroContactsPage from '@pages/Dashboard/actions/OrgXeroContactsPage/OrgXeroContactsPage'
import StaffAccessPage from '@pages/Dashboard/actions/StaffAccessPage'

const App = () => {
    const session = useSession()

    if (session) {
        return (
            <ThemeProvider theme={theme}>
                <Switch>
                    {/* home */}
                    <Route path="/" component={DashboardPage} />
                    {/* overviews */}
                    <Route path="/all-users" component={UsersOverviewPage} />
                    <Route path="/user-deepdive" component={UserPage} />
                    <Route
                        path="/organizations"
                        component={OrganizationsOverviewPage}
                    />
                    <Route
                        path="/org-collections"
                        component={OrgCollectionsPage}
                    />
                    <Route path="/sessions" component={SessionsOverviewPage} />
                    <Route
                        path="/denied-orgs"
                        component={OrganizationsDeniedOverviewPage}
                    />
                    <Route path="/podiums" component={PodiumsOverviewPage} />
                    <Route path="/podiums/:hardwareId" component={PodiumPage} />
                    {/* searches */}
                    <Route path="/users" component={UserSearchPage} />
                    <Route
                        path="/nte-invoice-search"
                        component={NTEInvoiceSearchPage}
                    />
                    {/* reports */}
                    <Route
                        path="/overdue-orders-report"
                        component={OverdueOrdersReportPage}
                    />
                    <Route
                        path="/completed-orders-report"
                        component={CompletedOrdersReportPage}
                    />
                    <Route
                        path="/billable-hours-report"
                        component={BillableHoursReportPage}
                    />
                    <Route
                        path="/subcontractor-hours-report"
                        component={SubcontractorHoursReportPage}
                    />
                    <Route path="/usage-report" component={UsageReportPage} />
                    <Route path="/organizations/:orgId" component={OrgPage} />
                    <Route path="/group/:groupId" component={GroupPage} />
                    <Route
                        path="/inactive-users-report"
                        component={InactiveUsersReport}
                    />
                    <Route
                        path="/eligible-users-report"
                        component={EligibleUsersReportPage}
                    />

                    {/* Actions */}
                    <Route path="/review-orgs" component={ReviewOrgsPage} />
                    <Route
                        path="/review-orgs/:orgId"
                        component={ReviewOrgPage}
                    />
                    <Route
                        path="/add-new-admin-to-org"
                        component={AddNewAdminToOrgPage}
                    />
                    <Route path="/send-invoice" component={SendInvoicesPage} />
                    <Route path="/transfer-user" component={TransferUser} />
                    <Route
                        path="/create-new-org"
                        component={CreateNewOrgPage}
                    />
                    <Route
                        path="/create-org-collection"
                        component={CreateOrgCollectionPage}
                    />
                    <Route
                        path="/edit-org-collection/:id"
                        component={EditOrgCollectionPage}
                    />
                    <Route
                        path="/merge-accounts"
                        component={MergeAccountsPage}
                    />
                    <Route
                        path="/create-uk-license"
                        component={CreateUKLicensesPage}
                    />
                    <Route
                        path="/manage-sso-connections"
                        component={ManageSsoConnectionsPage}
                    />
                    <Route
                        path="/create-sso-connection"
                        component={CreateSsoConnectionPage}
                    />
                    <Route
                        path="/edit-sso-connection/:id"
                        component={EditSsoConnectionPage}
                    />
                    <Route
                        path="/onboard-sso-tenant"
                        component={OnboardSsoTenantPage}
                    />
                    <Route path="/staff-access" component={StaffAccessPage} />
                    {/*
                      Lands here from a Test-login round-trip via
                      hl-accounts. Read-only confirmation page —
                      doesn't actually exchange the one-time code.
                    */}
                    <Route path="/sso/callback" component={SsoCallbackPage} />
                    <Route
                        path="/org-xero-contacts"
                        component={OrgXeroContactsPage}
                    />
                    {/* misc */}
                    <Route>404 Not Found</Route>
                </Switch>
            </ThemeProvider>
        )
    }

    return (
        <ThemeProvider theme={theme}>
            <Switch>
                <Route component={Login} />
            </Switch>
        </ThemeProvider>
    )
}

export default App
